import type { FlatBundle, LayoutResult } from "@/lib/types";

// In production (Firebase Hosting), VITE_API_BASE_URL points to the Cloud Run
// service. In development the Vite proxy rewrites /api → localhost, so an
// empty string makes relative /api URLs go through the proxy as before.
const API_BASE = `${import.meta.env.VITE_API_BASE_URL ?? ""}/api`;

/**
 * Extract a user-readable error message from a non-OK Response.
 *
 * FastAPI returns JSON like ``{"detail": "..."}`` for HTTPException; fall
 * back to the raw body or status line if the response isn't JSON.
 */
async function errorDetail(res: Response, fallback: string): Promise<string> {
  let body = "";
  try {
    body = await res.text();
  } catch {
    return `${fallback} (${res.status})`;
  }
  if (!body) return `${fallback} (${res.status})`;
  try {
    const parsed = JSON.parse(body);
    if (parsed && typeof parsed === "object" && typeof parsed.detail === "string") {
      return parsed.detail;
    }
  } catch {
    // not JSON
  }
  return body.slice(0, 500);
}

export async function convertPptx(file: File): Promise<FlatBundle> {
  const form = new FormData();
  form.append("file", file);

  const res = await fetch(`${API_BASE}/convert`, {
    method: "POST",
    body: form,
  });

  if (!res.ok) {
    const detail = await errorDetail(res, "変換に失敗しました");
    throw new Error(detail);
  }
  return (await res.json()) as FlatBundle;
}

export async function restoreFromBundle(bundle: FlatBundle): Promise<Blob> {
  const res = await fetch(`${API_BASE}/restore`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(bundle),
  });
  if (!res.ok) {
    const detail = await errorDetail(res, "復元に失敗しました");
    throw new Error(detail);
  }
  return await res.blob();
}

export async function restoreFromZip(zip: Blob): Promise<Blob> {
  const res = await fetch(`${API_BASE}/restore`, {
    method: "POST",
    headers: { "Content-Type": "application/zip" },
    body: zip,
  });
  if (!res.ok) {
    const detail = await errorDetail(res, "復元に失敗しました");
    throw new Error(detail);
  }
  return await res.blob();
}

export async function packBundle(bundle: FlatBundle): Promise<Blob> {
  const res = await fetch(`${API_BASE}/pack`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(bundle),
  });
  if (!res.ok) {
    const detail = await errorDetail(res, "ZIP 作成に失敗しました");
    throw new Error(detail);
  }
  return await res.blob();
}

// ─── Layout analysis ──────────────────────────────────────────────────────────

/** Upload a .pdf or .pptx and get its per-page layout analysis. */
export async function analyzeLayout(file: File): Promise<LayoutResult> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_BASE}/layout/analyze`, { method: "POST", body: form });
  if (!res.ok) {
    throw new Error(await errorDetail(res, "レイアウト解析に失敗しました"));
  }
  return (await res.json()) as LayoutResult;
}

/** URL of the lazily-rendered page image (PDF source only). */
export function layoutPageImageUrl(documentId: string, page: number): string {
  return `${API_BASE}/layout/${documentId}/page/${page}/image`;
}

/** Generate an editable .pptx from a PDF layout analysis (PDF→PowerPoint). */
export async function layoutToPptx(result: LayoutResult): Promise<Blob> {
  const res = await fetch(`${API_BASE}/layout/to-pptx`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(result),
  });
  if (!res.ok) {
    throw new Error(await errorDetail(res, "PowerPoint 生成に失敗しました"));
  }
  return await res.blob();
}
